import Rollup from "./rollup";
import Parameter from "./parameter";
import ModuleCardWidget from "./module-card-widget";
import ModuleCardScheduler from "./module-card-scheduler";
import ModuleCardPlanning from "./module-card-planning";
import ModuleCardList from "./module-card-list";
import ModuleCardGraphic from "./module-card-graphic";
import Module from "./module";
import Format from "./format";
import { sortBy } from "../utils";
import IRollupReturnData from "../interface/rollup-return-data";
import IModuleCard from "../interface/module-card";
import IModule from "../interface/module";
import ICardWidget from "../interface/card-widget";
import ICardGraphic from "../interface/card-graphic";
import IApiRollupData from "../interface/api/api-rollup-data";
import SortingType from "../enum/sorting-type";
import ParamStorage from "../enum/param-storage";
import GraphicDataSort from "../enum/graphic-data-sort";
import ErrorCode from "../enum/error-code";
import CardType from "../enum/card-type";
import ParameterStorage from "../class/parameter-storage";
import ModuleCardBase from "../class/module-card-base";

class ModuleCardInstance {

    /* Public Methods */

    public async delete(id: number): Promise<number> {
        const card: IModuleCard | null = this.get(id);
        if (card) {
            const controller: ModuleCardBase | null = this.getCardController(card);
            if (controller) {
                return await controller.delete(id);
            }
        }
        return id;
    }

    public async changeModule(cardId: number, moduleId: number): Promise<IModule> {
        const card = this.get(cardId);
        const mod = Module.get(moduleId);
        if (card && mod) {
            card.moduleId = moduleId;
            const controller: ModuleCardBase | null = this.getCardController(card);
            if (controller) {
                await controller.update(card);
            }
            return Module.get(moduleId) as IModule;
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async getData(cardId: number): Promise<IRollupReturnData> {
        const card: IModuleCard | null = this.get(cardId);
        if (card && (card.type === CardType.Widget || card.type === CardType.Graphic)) {
            let sortType: GraphicDataSort = GraphicDataSort.None;
            let rollupId: number | undefined;
            if (card.type === CardType.Widget) {
                rollupId = (card as ICardWidget).rollupId;
            } else if (card.type === CardType.Graphic) {
                rollupId = (card as ICardGraphic).rollupId;
                sortType = (card as ICardGraphic).options.dataSort;
            }
            if (rollupId) {
                const data: IRollupReturnData = await Rollup.getData(rollupId);
                data.data = this.cardDataSort(data.data, sortType);
                data.data = this.cardDataFormat(data);
                return data;
            } else {
                throw ErrorCode.BadArguments;
            }
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async clone(cardId: number): Promise<IModuleCard | null> {
        const card: IModuleCard | null = this.get(cardId);
        if (card) {
            const controller: ModuleCardBase | null = this.getCardController(card);
            if (controller) {
                return await controller.clone(card);
            }
        }
        return null;
    }

    /* Private Methods */

    public getAll(): IModuleCard[] {
        const result: IModuleCard[] = [];
        const invalid: IModuleCard[] = [];
        this.getStore().getAll().forEach((origin: IModuleCard) => {
            const card: IModuleCard | null = this.toClient(origin);
            if (card) {
                result.push(card);
            } else {
                invalid.push(origin);
            }
        });
        return result;
    }

    public get(id: number): IModuleCard | null {
        const card: IModuleCard | null = this.getStore().get(id);
        if (card) {
            return this.toClient(card);
        }
        return null;
    }

    public async deleteByListsIds(listsIds: number[]): Promise<number[]> {
        const cardsIds: number[] = [];
        this.getAll().filter((card: IModuleCard) => {
            const controller: ModuleCardBase | null = this.getCardController(card);
            if (controller) {
                const listId: number | null = controller.getListId(card);
                if (listId && listsIds.includes(listId)) {
                    cardsIds.push(card.id as number);
                }
            }
        })
        await this.getStore().bulk([], [], cardsIds);
        return cardsIds;
    }

    public async rename(id: number, label: string): Promise<IModuleCard | null> {
        const card: IModuleCard | null = this.getStore().get(id);
        if (card) {
            card.label = label;
            await this.getStore().update(card);
            return this.get(id);
        }
        return null;
    }

    private toClient(card: IModuleCard): IModuleCard | null {
        const controller: ModuleCardBase | null = this.getCardController(card);
        if (controller) {
            return controller.toClient(card);
        }
        return null;
    }

    private cardDataSort(data: IApiRollupData[], sortType: GraphicDataSort): IApiRollupData[] {
        switch(sortType) {
            case GraphicDataSort.ValueAsc:
                sortBy(data, 'value', SortingType.Ascending);
                break;
            case GraphicDataSort.ValueDesc:
                sortBy(data, 'value', SortingType.Descending);
                break;
            case GraphicDataSort.LabelAsc:
                sortBy(data, 'label', SortingType.Ascending);
                break;
            case GraphicDataSort.LabelDesc:
                sortBy(data, 'label', SortingType.Descending);
                break;
        }
        return data;
    }

    private cardDataFormat(data: IRollupReturnData): IApiRollupData[] {
        return data.data.map((item: IApiRollupData) => {
            return {
                label: Format.byFieldId(item.label || '', data.fieldLabelId || -1),
                value: item.value
            };
        });
    }

    private getCardController(card: IModuleCard): ModuleCardBase | null {
        switch (card.type) {
            case CardType.List:
                return ModuleCardList;
            case CardType.Widget:
                return ModuleCardWidget;
            case CardType.Graphic:
                return ModuleCardGraphic;
            case CardType.Planning:
                return ModuleCardPlanning;
            case CardType.Scheduler:
                return ModuleCardScheduler;
            default:
                return null;
        }
    }

    /* Private Methods - Parameter Storage */

    private getStore(): ParameterStorage<IModuleCard> {
        return Parameter.get(ParamStorage.ModuleCards);
    }

}

const ModuleCard = new ModuleCardInstance();

export default ModuleCard;